import { List, ListItem, MenuButton, mergeClasses, SelectionItemId } from '@fluentui/react-components';
import React, { useState } from 'react';
import { IFilterItem } from '../../../data-types/filterItem';
import { getFilterItemDisplayLabel } from '../../../utils/FilterUtils';
import ContextualMenuClearSelectionItem from '../../common/ContextualMenuClearSelectionItem';
import TabList from '../../tab-list/TabList';
import FilterActionBar from '../filter-actions/FilterActionBar';
import FilterSearchBar from '../filter-search-bar/FilterSearchBar';
import FilterPicker, { useFilterPickerStyles } from './FilterPicker';

export interface IFilterPickerPopoverProps {
    buttonId: string;
    label: string;
    ariaLabel?: string;
    isActive?: boolean;
    isDisabled?: boolean;
    filterMenuAriaLabel: string;
    searchBoxPlaceholderText: string;
    clearAllText: string;
    isMultiSelect?: boolean;
    draftSelectedIds: SelectionItemId[];
    visibleItems: IFilterItem[];
    hasResults: boolean;
    noResultsText: string;
    announcementMessage: string;
    showClearAll: boolean;
    itemIcon?: React.ReactNode | null;
    tabsLabels?: string[];
    selectedTabIndex?: number;
    onTabChange?: (tabIndex: number) => void;
    onSearchChange: (newValue: string | undefined) => void;
    onAbortSearch: () => void;
    onSelectionChange: (selectedIds: SelectionItemId[]) => void;
    onSingleSelectItem?: (item: IFilterItem) => void;
    onClearAll: () => void;
    onApply: () => void;
    onCancel: () => void;
    onDismiss: () => void;
}

/**
 * Prop-driven recipe over {@link FilterPicker} for standard multi/single-select filters.
 * Prefer composing `FilterPicker` slots directly when rows/footer need custom UI (folders).
 */
const FilterPickerPopover: React.FC<IFilterPickerPopoverProps> = ({
    buttonId,
    label,
    ariaLabel,
    isActive,
    isDisabled,
    filterMenuAriaLabel,
    searchBoxPlaceholderText,
    clearAllText,
    isMultiSelect = true,
    draftSelectedIds,
    visibleItems,
    hasResults,
    noResultsText,
    announcementMessage,
    showClearAll,
    itemIcon,
    tabsLabels,
    selectedTabIndex,
    onTabChange,
    onSearchChange,
    onAbortSearch,
    onSelectionChange,
    onSingleSelectItem,
    onClearAll,
    onApply,
    onCancel,
    onDismiss,
}) => {
    const filterPickerStyles = useFilterPickerStyles();
    const [isOpen, setIsOpen] = useState(false);
    const isMultitab = Boolean(tabsLabels && tabsLabels.length > 0);
    const applyButtonId = `${buttonId}-apply`;

    // Controlled open: setIsOpen(false) does not fire Popover onOpenChange, so call onDismiss here too.
    const dismissPicker = () => {
        setIsOpen(false);
        onDismiss();
    };

    const handleApply = () => {
        onApply();
        dismissPicker();
    };

    const handleCancel = () => {
        onCancel();
        dismissPicker();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter' || !isMultiSelect) {
            return;
        }
        const target = event.target as HTMLElement;
        if (target.closest('[role="option"]') || target.closest('input[type="checkbox"]')) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        document.getElementById(applyButtonId)?.click();
    };

    const handleSingleSelectClick = (item: IFilterItem) => {
        onSingleSelectItem?.(item);
        dismissPicker();
    };

    const selectedIdSet = new Set(draftSelectedIds.map(String));

    const listContent = !hasResults ? (
        <div className={filterPickerStyles.noResults}>{noResultsText}</div>
    ) : (
        <List
            className={filterPickerStyles.list}
            selectionMode={isMultiSelect ? 'multiselect' : 'single'}
            selectedItems={draftSelectedIds}
            onSelectionChange={(_ev, data) => {
                if (isMultiSelect) {
                    onSelectionChange(data.selectedItems);
                    return;
                }
                const selectedKey = data.selectedItems[0];
                if (selectedKey == null) {
                    return;
                }
                const item = visibleItems.find((candidate) => String(candidate.key) === String(selectedKey));
                if (item) {
                    handleSingleSelectClick(item);
                }
            }}
            aria-label={filterMenuAriaLabel}
        >
            {visibleItems.map((item, index) => {
                const displayLabel = getFilterItemDisplayLabel(item);
                const isChecked = selectedIdSet.has(item.key);
                return (
                    <ListItem
                        key={item.key}
                        id={`item-${index}`}
                        value={item.key}
                        className={mergeClasses(filterPickerStyles.listItem, isChecked && filterPickerStyles.listItemChecked)}
                    >
                        {!isMultiSelect && itemIcon}
                        {displayLabel}
                    </ListItem>
                );
            })}
        </List>
    );

    return (
        <FilterPicker
            open={isOpen}
            onOpenChange={setIsOpen}
            onDismiss={onDismiss}
            surfaceAriaLabel={filterMenuAriaLabel}
            contentAriaLabel={filterMenuAriaLabel}
            onContentKeyDown={handleKeyDown}
            trigger={
                <MenuButton
                    id={buttonId}
                    aria-label={ariaLabel ?? label}
                    aria-expanded={isOpen}
                    aria-haspopup='dialog'
                    disabled={isDisabled}
                    disabledFocusable={isDisabled}
                    appearance='secondary'
                    className={mergeClasses(isActive && filterPickerStyles.activeTrigger, isOpen && filterPickerStyles.openTrigger)}
                    data-active={isActive ? 'true' : 'false'}
                >
                    {label}
                </MenuButton>
            }
            tabs={
                isMultitab ? (
                    <TabList
                        tabs={tabsLabels!}
                        activeTabIndex={selectedTabIndex ?? 0}
                        setActiveTabIndex={onTabChange!}
                        label='Filter tabs'
                    />
                ) : undefined
            }
            announcementMessage={announcementMessage}
            search={
                <FilterSearchBar
                    searchBoxPlaceholderText={searchBoxPlaceholderText}
                    onSearchBoxChange={(_ev, newValue) => onSearchChange(newValue)}
                    onAbort={onAbortSearch}
                />
            }
            clearSelection={
                showClearAll ? (
                    <ContextualMenuClearSelectionItem text={clearAllText} dismissMenu={dismissPicker} onViewAllClick={onClearAll} />
                ) : undefined
            }
            list={listContent}
            footer={
                isMultiSelect ? (
                    <FilterActionBar
                        primaryHandler={handleApply}
                        primaryButtonLabel='apply'
                        primaryButtonId={applyButtonId}
                        secondaryButtonLabel='cancel'
                        secondaryHandler={handleCancel}
                    />
                ) : undefined
            }
        />
    );
};

export default FilterPickerPopover;
